/**
 * 공용 HTTP 클라이언트
 *
 * 모든 도메인 API 서비스가 공통으로 사용하는 HTTP 요청 유틸리티.
 * 인증 토큰 자동 첨부, 일시적 오류에 대한 지수 백오프 재시도 기능 포함.
 */

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY = 1000;
const DEFAULT_TIMEOUT = 30000;

/**
 * Sleep for a given duration.
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Calculate delay for exponential backoff with jitter.
 * @param {number} attempt - Current attempt number (0-based)
 * @param {number} baseDelay - Base delay in ms
 * @returns {number} Delay in ms
 */
function getBackoffDelay(attempt, baseDelay) {
  const exponential = baseDelay * Math.pow(2, attempt);
  const jitter = exponential * 0.1 * Math.random();
  return exponential + jitter;
}

/**
 * Check if an HTTP method is safe to retry (idempotent).
 * @param {string} method - HTTP method
 * @returns {boolean}
 */
function isSafeToRetry(method) {
  const safeMethod = (method || 'GET').toUpperCase();
  return safeMethod === 'GET' || safeMethod === 'HEAD' || safeMethod === 'OPTIONS';
}

/**
 * Make an authenticated API request with optional retry.
 *
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @param {Object} options.retry - Enable retry (default: true for GET, false for others)
 * @param {number} options.maxRetries - Max retry attempts (default: 3)
 * @param {number} options.timeout - Request timeout in ms (default: 30000)
 * @param {Object} logger - Logger instance (default: console)
 * @returns {Promise<any>} Response data
 */
export async function apiRequest(url, options = {}, logger = console) {
  const {
    retry,
    maxRetries = DEFAULT_MAX_RETRIES,
    timeout = DEFAULT_TIMEOUT,
    ...fetchOptions
  } = options;

  const shouldRetry = retry !== undefined ? retry : isSafeToRetry(fetchOptions.method);

  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const mergedOptions = {
    ...defaultOptions,
    ...fetchOptions,
    headers: { ...defaultOptions.headers, ...fetchOptions.headers },
  };

  let lastError;

  for (let attempt = 0; attempt <= (shouldRetry ? maxRetries : 0); attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        ...mergedOptions,
        signal: mergedOptions.signal || controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `HTTP ${response.status}`);
        error.status = response.status;
        error.data = errorData;
        error.retryAfter = response.headers.get('Retry-After');

        if (shouldRetry && attempt < maxRetries && RETRYABLE_STATUS_CODES.has(response.status)) {
          lastError = error;
          const retryAfterMs = error.retryAfter ? parseInt(error.retryAfter, 10) * 1000 : null;
          const delay = retryAfterMs || getBackoffDelay(attempt, DEFAULT_BASE_DELAY);
          logger.warn(
            `Retry ${attempt + 1}/${maxRetries} for ${url} (HTTP ${response.status}) in ${Math.round(delay)}ms`
          );
          await sleep(delay);
          continue;
        }

        throw error;
      }

      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        const timeoutError = new Error(`Request timeout after ${timeout}ms`);
        timeoutError.status = 408;
        throw timeoutError;
      }

      if (
        shouldRetry &&
        attempt < maxRetries &&
        !error.status // Network errors (no HTTP status)
      ) {
        lastError = error;
        const delay = getBackoffDelay(attempt, DEFAULT_BASE_DELAY);
        logger.warn(
          `Retry ${attempt + 1}/${maxRetries} for ${url} (network error) in ${Math.round(delay)}ms`
        );
        await sleep(delay);
        continue;
      }

      logger.error('API request failed:', error);
      throw error;
    }
  }

  logger.error('API request failed after all retries:', lastError);
  throw lastError;
}

/**
 * Return the URL unchanged (cookies are sent automatically via credentials: 'include').
 *
 * @param {string} url - Base URL
 * @returns {string} URL unchanged
 */
export function appendTokenToUrl(url) {
  return url;
}

/**
 * Build standard auth headers with optional token.
 *
 * @param {string|null} tenantId - Optional tenant ID header
 * @param {Object} extra - Additional headers to merge
 * @returns {Object} Headers object
 */
export function buildAuthHeaders(tenantId = null, extra = {}) {
  return {
    'Content-Type': 'application/json',
    ...(tenantId && { 'X-Tenant-Id': tenantId }),
    ...extra,
  };
}

/**
 * Authenticated fetch wrapper that always includes credentials for httpOnly cookies.
 *
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export function authFetch(url, options = {}) {
  return fetch(url, {
    ...options,
    credentials: 'include',
  });
}
